import { t } from 'i18next';
import { Option, StatusOption } from 'shared/types/general';
import { ContractLine } from '../../types';
import { UpdateContractLineForm } from '../form/UpdateContractLineForm';
import toast from 'shared/utils/toast';
import { contractsApi } from '../../api/contracts.api';
import { IProduct } from 'features/inventory/products/types';
import { useEffect, useState } from 'react';
import { EnhancedDialog } from 'components/enhanced';

interface UpdateContractLineDialogProps {
  contractLine: ContractLine | null;
  handleRefreshDataGrid: () => void;
  onClose: () => void;
  open: boolean;
  warehouses: Option[];
  contractCode: string;
}

export const UpdateContractLineDialog = ({
  contractLine,
  handleRefreshDataGrid,
  onClose,
  open,
  warehouses,
  contractCode
}: UpdateContractLineDialogProps) => {
  const TRANSLATION_PREFIX = 'pages.contract-line.edit';
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);

  useEffect(() => {
    if (!open || !contractLine) {
      return;
    }

    contractsApi
      .getProductWarehouseOptions()
      .then((response) => {
        const nextOptions =
          response?.actions?.POST?.status?.choices?.filter((item) =>
            Object.keys({
              ACTIVE: 1,
              DECOMMISSIONED: 2,
              TO_BE_DECOMMISSIONED: 3,
              TENDER: 4
            }).includes(item.display_name)
          ) || [];

        setStatusOptions(
          nextOptions.map((item) => ({
            ...item,
            value: Number(item.value ?? item.display_name),
            display_name: item.display_name
          }))
        );
      })
      .catch(() => {
        setStatusOptions([]);
      });
  }, [contractLine, open]);

  const handleContractLineEdit = async (id, params) => {
    const { status, ...updateParams } = params;

    try {
      await contractsApi.services.contractLineService.update(id, updateParams);

      if (status) {
        await contractsApi.services.contractLineService.setStatus(id, status);
      }
      toast.crud.successUpdate('Contratto');
      handleRefreshDataGrid();
    } finally {
      onClose();
    }
  };

  return (
    <EnhancedDialog
      open={open}
      onClose={onClose}
      title={t(`pages.contract-line.edit.title`)}
      subTitle={
        contractLine &&
        t(`${TRANSLATION_PREFIX}.body`, {
          product: (contractLine.product as IProduct).code,
          contract: contractCode
        })
      }
      submitLabel={t('actions.save', 'Salva')}
      cancelLabel={t('actions.reset', 'Annulla')}
      maxWidth="lg"
      submitButtonProps={{
        type: 'submit',
        form: 'updateContracLine'
      }}>
      {contractLine && (
        <UpdateContractLineForm
          contractLine={contractLine}
          warehouses={warehouses}
          statusOptions={statusOptions}
          onSubmit={handleContractLineEdit}
        />
      )}
    </EnhancedDialog>
  );
};
